/**
 * Where a signed-out visitor goes: Create account on a new install that has
 * no accounts yet, Sign in everywhere else. `IdentityGate` renders it when
 * there is no session.
 *
 * It asks the server fresh each time it mounts. The answer can change while
 * the page is open: the first account may be made in this tab or another,
 * and a saved answer would keep sending the next visit back to Create
 * account. When the answer can't be read, it goes to Sign in, which is what
 * every signed-out visit did before this check existed.
 *
 * The page the visitor was after travels as the `returnTo` query parameter,
 * which both screens read, with its own query string. `/` means no
 * particular page, so it adds nothing.
 *
 * Its own module, beside `RequireSuperadmin`, so a test can render it
 * without importing `App` and its provider tree.
 */
import { Navigate, useLocation } from 'react-router-dom'
import { usePublicSettings } from '@/hooks/usePublicSettings'

export function SignedOutRedirect() {
  const location = useLocation()
  const publicSettings = usePublicSettings({ fresh: true })

  if (publicSettings.status === 'loading') {
    return (
      <div className="min-h-dvh flex items-center justify-center text-muted-foreground text-sm">
        Loading…
      </div>
    )
  }

  const firstAccount = publicSettings.status === 'ready' && publicSettings.settings.firstAccount
  const screen = firstAccount ? '/auth/signup' : '/auth/login'
  const destination = location.pathname + location.search
  const to = destination === '/' ? screen : `${screen}?returnTo=${encodeURIComponent(destination)}`
  return <Navigate to={to} replace />
}
